import React, { useState, useContext, useEffect } from 'react';
import { useMutation } from '@apollo/client';
import { Box, Button, Flex, Flow, Text, theme } from '@xometry/xometry_loft';
import {
  GetOrganizationInvitationsQuery,
  SendOrganizationInvitationsMutationResult,
  SendOrganizationInvitationsMutationVariables,
  SendOrganizationInvitationsQuery,
} from '../../../schema/generated/schema';
import { AuthContext } from '@xometry/auth-context';
import EmailSelectCreatable from '../../../components/EmailSelectCreatable';
import quotesApi from '../../../services/quotes';

interface InvitePanelProps {
  orgName: string | undefined;
  orgId: number | undefined;
  onSuccess: (c: number) => void;
  onError: () => void;
}
const InvitePanel = (props: InvitePanelProps) => {
  const { orgName, orgId, onSuccess, onError } = props;
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [invitationEmailList, setInvitationEmailList] = useState([]);
  const [isValidInvitationList, setIsValidInvitationList] = useState(false);
  const [smartInvites, setSmartInvites] = useState<string[]>([]);
  const [submissionErrors, setSubmissionErrors] = useState([]);
  const { user, isStaff } = useContext(AuthContext);
  const [sendOrganizationInvitations] = useMutation<
    SendOrganizationInvitationsMutationResult,
    SendOrganizationInvitationsMutationVariables
  >(SendOrganizationInvitationsQuery, {
    refetchQueries: [
      GetOrganizationInvitationsQuery,
      'GetOrganizationInvitations',
    ],
  });
  useEffect(() => {
    const getSmartInvites = async () => {
      if (user?.id) {
        const shareableEntities = await quotesApi.getShareableEntities(user.id);
        const { users = [] } = shareableEntities;
        setSmartInvites([...users]);
      }
    };

    getSmartInvites();
  }, [user]);

  const formSubmit = async () => {
    if (invitationEmailList.length === 0) {
      setSubmissionErrors([...submissionErrors, 'blankEmail']);
      return;
    } else if (!isValidInvitationList) {
      setSubmissionErrors(['invalidEmail']);
    } else if (
      isValidInvitationList ||
      (!isValidInvitationList && invitationEmailList.length === 0)
    ) {
      setSubmissionErrors([]);
      setIsSubmitting(true);
      // Convert all emails to lowercase
      const lowerCaseEmails = invitationEmailList.map((email: string) =>
        email.toLowerCase()
      );
      const { data: inviteResult } = await sendOrganizationInvitations({
        variables: {
          addUsersInput: {
            organizationId: `${orgId}`,
            emails: lowerCaseEmails,
            permissionIds: [],
          },
        },
      });
      if (inviteResult.sendOrganizationInvitations?.length > 0) {
        onSuccess(inviteResult.sendOrganizationInvitations.length);
        if (window.analytics && user) {
          window.analytics.track(
            'Xometry Organization User Invited To Organization',
            {
              isStaff: isStaff(),
              userId: user.id,
              organizationId: orgId,
              emails: lowerCaseEmails.toString(),
              panel: 'Team Member Management',
            }
          );
        }
        setInvitationEmailList([]);
      } else {
        onError();
      }
      setIsSubmitting(false);
    }
  };
  return (
    <Flex
      flexDirection="column"
      color={theme.colors.darkBlues[90]}
      backgroundColor={theme.colors.white}
    >
      <Box>
        <Box mb={theme.space[2]}>
          <Text kind="panel-title">Invite Team Members to {orgName}</Text>
        </Box>
        <Box>
          Send invitations to your colleagues by entering their email address
          below.
        </Box>
      </Box>
      <Box mt="48px">
        <EmailSelectCreatable
          isModal
          suggestions={smartInvites}
          label="Add Members"
          setSelectedEmails={setInvitationEmailList}
          setAreEmailsValid={setIsValidInvitationList}
        />
      </Box>
      <Flex justifyContent="flex-end" height="34px">
        <Button
          kind={theme.kind.primary}
          fontWeight={theme.fontWeights.regular}
          disabled={isSubmitting}
          onClick={formSubmit}
        >
          {isSubmitting ? <Flow /> : 'Send Invitations'}
        </Button>
      </Flex>
    </Flex>
  );
};

export default InvitePanel;
